/** Company labor pay: order line → rate × Pays by. Not individual/bay payroll. */

import {
  isLaborLineIncluded,
  laborPayAmountUsd,
  laborPayCount,
  type LaborPayBuildingSize,
  type LaborPaysBy,
} from "./laborPayQuantity.js";

export type LaborOrderLine = {
  key: string;
  option_key: string;
  product_key: string;
  label: string;
  quantity: number;
  /** IdeaRoom retail; null when the payload omitted price. 0 / negative = included. */
  price: number | null;
};

export type LaborRateMatchInput = {
  id: string;
  item_code: string;
  option_code: string;
  label: string;
  recipe_id: string | null;
  is_active: boolean;
  flat_rate_usd: number;
  pays_by: LaborPaysBy | null;
};

export type LaborLineLaborStatus = "matched" | "unmatched" | "included" | "needs_pays_by" | "needs_size";

export type ModelCatalogEntry = {
  id: string;
  kind: "recipe" | "named_variant";
  code: string;
  name: string;
};

export type LaborMatchedLine = {
  line: LaborOrderLine;
  rate: LaborRateMatchInput;
  amount_usd: number;
};

export type LaborCompanyInvoice = {
  payout_usd: number;
  rate_item_ids: string[];
  matched: LaborMatchedLine[];
  unmatched_labor: LaborOrderLine[];
};

export type LaborLineMatchStatus = {
  line: LaborOrderLine;
  labor: LaborLineLaborStatus;
  labor_rate_id: string | null;
  amount_usd: number;
  count: number;
  model: "matched" | "unmatched";
  model_label: string | null;
};

const BASE_PRODUCT_KEYS = new Set([
  "size",
  "style",
  "style-group",
  "style_group",
  "wall-height",
  "wall_height",
  "height",
]);

export function normalizeLaborMatchKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asStr(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asQty(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 1;
}

function asPrice(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function lineIdentity(optionKey: string, productKey: string, label: string): string {
  return optionKey || productKey || normalizeLaborMatchKey(label) || label;
}

function pushLine(out: LaborOrderLine[], line: LaborOrderLine): void {
  if (!line.label && !line.option_key) return;
  const key = lineIdentity(line.option_key, line.product_key, line.label);
  const existing = out.find((row) => row.key === key);
  if (existing) {
    existing.quantity += line.quantity;
    if (existing.price == null) existing.price = line.price;
    else if (line.price != null && line.price > 0) existing.price = line.price;
    return;
  }
  out.push({ ...line, key });
}

function parseLineItem(item: Record<string, unknown>): LaborOrderLine | null {
  const option_key = asStr(item.option_key) || asStr(item.optionKey);
  const product_key = asStr(item.product_key) || asStr(item.productKey);
  const label =
    asStr(item.label) ||
    asStr(item.value) ||
    asStr(item.model) ||
    asStr(item.description);
  if (!label && !option_key) return null;
  return {
    key: lineIdentity(option_key, product_key, label),
    option_key,
    product_key,
    label: label || option_key,
    quantity: asQty(item.quantity),
    price: asPrice(item.price),
  };
}

function parseLineItemList(raw: unknown): LaborOrderLine[] {
  if (!Array.isArray(raw)) return [];
  const out: LaborOrderLine[] = [];
  for (const item of raw) {
    const rec = asRecord(item);
    if (!rec) continue;
    const line = parseLineItem(rec);
    if (line) pushLine(out, line);
  }
  return out;
}

/** Pull billable lines from unit/deal options (IdeaRoom compact or raw). */
export function extractLaborOrderLines(options: Record<string, unknown> | null | undefined): LaborOrderLine[] {
  const root = options ?? {};
  const nestedOrder = asRecord(root.order) ?? root;
  const fromCompact = parseLineItemList(root.line_items);
  const fromRaw = parseLineItemList(nestedOrder.lineItems);
  const out: LaborOrderLine[] = [];
  for (const line of fromCompact.length ? fromCompact : fromRaw) pushLine(out, line);

  const style = asStr(root.style);
  if (style && !out.some((l) => l.product_key === "style")) {
    pushLine(out, {
      key: "style",
      option_key: "",
      product_key: "style",
      label: style,
      quantity: 1,
      price: null,
    });
  }
  const size = asStr(root.size);
  if (size && !out.some((l) => l.product_key === "size")) {
    pushLine(out, {
      key: "size",
      option_key: "",
      product_key: "size",
      label: size,
      quantity: 1,
      price: null,
    });
  }
  return out;
}

export function isBaseBuildingLine(line: LaborOrderLine): boolean {
  return BASE_PRODUCT_KEYS.has(line.product_key);
}

function rateKeys(rate: LaborRateMatchInput): string[] {
  return [rate.option_code, rate.item_code, rate.label]
    .map(normalizeLaborMatchKey)
    .filter(Boolean);
}

function lineKeys(line: LaborOrderLine): string[] {
  return [line.option_key, line.key, line.label].map(normalizeLaborMatchKey).filter(Boolean);
}

export function findLaborRate(
  line: LaborOrderLine,
  rates: readonly LaborRateMatchInput[]
): LaborRateMatchInput | null {
  const keys = lineKeys(line);
  if (keys.length === 0) return null;
  const active = rates.filter((r) => r.is_active);
  for (const rate of active) {
    const rk = rateKeys(rate);
    if (keys.some((k) => rk.includes(k))) return rate;
  }
  return null;
}

function catalogKeys(entry: ModelCatalogEntry): string[] {
  return [entry.code, entry.name].map(normalizeLaborMatchKey).filter(Boolean);
}

export function findModelMatch(
  line: LaborOrderLine,
  catalog: readonly ModelCatalogEntry[]
): ModelCatalogEntry | null {
  const keys = lineKeys(line);
  if (keys.length === 0) return null;
  for (const entry of catalog) {
    const ck = catalogKeys(entry);
    if (keys.some((k) => ck.includes(k))) return entry;
  }
  return null;
}

export function isLaborLineOpen(status: LaborLineLaborStatus): boolean {
  return status === "unmatched" || status === "needs_pays_by" || status === "needs_size";
}

function evaluateLaborLine(
  line: LaborOrderLine,
  rate: LaborRateMatchInput | null,
  building: LaborPayBuildingSize | null
): { labor: LaborLineLaborStatus; amount_usd: number; count: number; labor_rate_id: string | null } {
  if (isBaseBuildingLine(line) || isLaborLineIncluded(line.price)) {
    return { labor: "included", amount_usd: 0, count: 0, labor_rate_id: null };
  }
  if (!rate) return { labor: "unmatched", amount_usd: 0, count: 0, labor_rate_id: null };
  if (!rate.pays_by) {
    return { labor: "needs_pays_by", amount_usd: 0, count: 0, labor_rate_id: rate.id };
  }
  const count = laborPayCount(rate.pays_by, line.quantity, building);
  if (count == null) {
    return { labor: "needs_size", amount_usd: 0, count: 0, labor_rate_id: rate.id };
  }
  return {
    labor: "matched",
    amount_usd: laborPayAmountUsd(rate.flat_rate_usd, count),
    count,
    labor_rate_id: rate.id,
  };
}

export function laborCompanyInvoice(input: {
  lines: readonly LaborOrderLine[];
  rates: readonly LaborRateMatchInput[];
  recipeId: string | null;
  building?: LaborPayBuildingSize | null;
}): LaborCompanyInvoice {
  const matched: LaborMatchedLine[] = [];
  const unmatched_labor: LaborOrderLine[] = [];
  const used = new Set<string>();
  const building = input.building ?? null;

  for (const line of input.lines) {
    const rate = findLaborRate(line, input.rates);
    const evaled = evaluateLaborLine(line, rate, building);
    if (evaled.labor === "matched" && rate) {
      used.add(rate.id);
      matched.push({ line, rate, amount_usd: evaled.amount_usd });
      continue;
    }
    if (isLaborLineOpen(evaled.labor)) unmatched_labor.push(line);
  }

  if (input.recipeId) {
    for (const rate of input.rates) {
      if (!rate.is_active || rate.recipe_id !== input.recipeId || used.has(rate.id)) continue;
      const skuLine: LaborOrderLine = {
        key: rate.item_code || rate.id,
        option_key: rate.option_code,
        product_key: "sku",
        label: rate.label,
        quantity: 1,
        price: null,
      };
      const evaled = evaluateLaborLine(skuLine, rate, building);
      if (evaled.labor !== "matched") continue;
      used.add(rate.id);
      matched.push({ line: skuLine, rate, amount_usd: evaled.amount_usd });
    }
  }

  const payout_usd = laborPayAmountUsd(1, matched.reduce((s, m) => s + m.amount_usd, 0));
  return {
    payout_usd,
    rate_item_ids: matched.map((m) => m.rate.id),
    matched,
    unmatched_labor,
  };
}

export function laborLineMatchStatuses(input: {
  lines: readonly LaborOrderLine[];
  rates: readonly LaborRateMatchInput[];
  catalog: readonly ModelCatalogEntry[];
  recipeId: string | null;
  recipeLabel?: string | null;
  building?: LaborPayBuildingSize | null;
}): LaborLineMatchStatus[] {
  const building = input.building ?? null;
  return input.lines.map((line) => {
    const rate = findLaborRate(line, input.rates);
    const evaled = evaluateLaborLine(line, rate, building);
    const model =
      (input.recipeId && isBaseBuildingLine(line)
        ? { id: input.recipeId, kind: "recipe" as const, code: "", name: input.recipeLabel ?? "SKU" }
        : null) ?? findModelMatch(line, input.catalog);
    return {
      line,
      labor: evaled.labor,
      labor_rate_id: evaled.labor_rate_id,
      amount_usd: evaled.amount_usd,
      count: evaled.count,
      model: model ? "matched" : "unmatched",
      model_label: model ? model.name || model.code : null,
    };
  });
}
